import { useQuery } from '@tanstack/react-query';
import { billingApi } from '../../api/endpoints';
import type { PlanIncludes } from '../../api/types';
import { useI18n } from '../../i18n';
import { Radio } from '../../ui';
import { PaymentsNotice } from '../growth/payments';
import type { StartChoice as Choice } from '../growth/signup-plan';

// Registering a company (TF-401): 30 days with everything, or the Free plan with no end date. The allowances
// come from the price list, never typed in. A number that has had a trial is offered Free only.

function useAllowances() {
  const { t, bytes } = useI18n();
  return (includes: PlanIncludes) =>
    t('company.startAllowances', { storage: bytes(includes.storageBytes), members: includes.members, signatures: includes.signatures, ocrPages: includes.ocrPages });
}

export function StartChoice({ value, onChange, trialUsed }: { value: Choice; onChange: (choice: Choice) => void; trialUsed: boolean }) {
  const { t } = useI18n();
  const allowances = useAllowances();
  const list = useQuery({ queryKey: ['public-plans'], queryFn: () => billingApi.publicPlans(), staleTime: 5 * 60_000 });
  const free = list.data?.plans.find((plan) => plan.id === 'free');
  const trial = list.data?.trial;
  const freeWords = free ? allowances(free.includes) : '';
  return (
    <fieldset className="start-choice" role="radiogroup" aria-labelledby="start-choice-title">
      <legend id="start-choice-title">{t('company.startTitle')}</legend>
      {trialUsed ? (
        <p className="hint">{t('company.startTrialUsed')}</p>
      ) : (
        <div className={value === 'trial' ? 'start-choice-card is-chosen' : 'start-choice-card'}>
          <Radio
            name="company-start"
            value="trial"
            checked={value === 'trial'}
            onChange={() => onChange('trial')}
            label={t('company.startTrial')}
            hint={
              <>
                {trial ? allowances(trial.includes) : ''}
                {free && <span className="start-choice-then">{t('company.startTrialThen', { free: freeWords })}</span>}
              </>
            }
          />
        </div>
      )}
      <div className={value === 'free' || trialUsed ? 'start-choice-card is-chosen' : 'start-choice-card'}>
        <Radio
          name="company-start"
          value="free"
          checked={value === 'free' || trialUsed}
          onChange={() => onChange('free')}
          label={t('company.startFree')}
          hint={
            <>
              {freeWords}
              <span className="start-choice-then">{t(trialUsed ? 'company.startFreeNoEnd' : 'company.startFreeLater')}</span>
            </>
          }
        />
      </div>
      <PaymentsNotice />
    </fieldset>
  );
}
